import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addMoney, Banner, Button, Card, compareMoney, Dialog, Field, isZero, Money, negate, PageHeader, Pill } from '@elysian/ui';
import type { Account, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso } from '../invoices/shared';

const CLASSES: { key: Account['class']; label: string; normal: 'debit' | 'credit' }[] = [
  { key: 'asset', label: 'Assets', normal: 'debit' },
  { key: 'liability', label: 'Liabilities', normal: 'credit' },
  { key: 'equity', label: 'Capital and reserves', normal: 'credit' },
  { key: 'income', label: 'Income', normal: 'credit' },
  { key: 'expense', label: 'Expenses', normal: 'debit' },
];

const CONTROL: Partial<Record<Account['control'], string>> = {
  debtors: 'Debtors control', creditors: 'Creditors control', bank: 'Bank', vat: 'VAT', retained_earnings: 'Retained earnings',
  suspense: 'Suspense', opening_balance: 'Opening balances', fx_gain_loss: 'Exchange differences',
};

/** Balances shown the way an accountant reads them: credit-side accounts as positive figures. */
export function naturalBalance(a: Pick<Account, 'class' | 'balance'>): string {
  const b = a.balance ?? '0';
  return CLASSES.find((c) => c.key === a.class)?.normal === 'credit' ? negate(b) : b;
}

export function ChartOfAccounts() {
  const { source, today, can } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  // #36: balances, and a total for each group, when ticked; remembered on this device.
  const [showBalances, setShowBalances] = useState(() => { try { return localStorage.getItem('ledger.chart.balances') === '1'; } catch { return false; } });
  const toggleBalances = (on: boolean) => { setShowBalances(on); try { localStorage.setItem('ledger.chart.balances', on ? '1' : '0'); } catch { /* not kept */ } };
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? ''); // a link from search arrives filtered
  const [editing, setEditing] = useState<Account | 'new' | null>(null);
  const data = useAsync(async () => {
    const [accounts, codes] = await Promise.all([source.accounts(undefined, true), source.taxCodes(iso(today()))]);
    return { accounts, codes };
  }, [source, version], { keep: true });
  const accounts = data.state === 'ready' ? data.data.accounts : [];
  const shown = useMemo(() => accounts
    .filter((a) => showInactive || a.is_active)
    .filter((a) => !q.trim() || `${a.code} ${a.name}`.toLowerCase().includes(q.trim().toLowerCase())), [accounts, showInactive, q]);
  const inactive = accounts.filter((a) => !a.is_active).length;
  const mayEdit = can('settings.accounts');

  return (
    <>
      <PageHeader title="Chart of accounts" subtitle="Codes and types are fixed once used; names, VAT defaults and on/off can change."
        actions={mayEdit ? <Button variant="primary" disabled={data.state !== 'ready'} onClick={() => setEditing('new')}>New account</Button> : undefined} />
      {data.state === 'error' && <Banner>Couldn’t load accounts: {data.error.message}</Banner>}
      <Card>
        <div className="el-spread" style={{ padding: '14px 16px' }}>
          <label className="el-search" style={{ flex: '0 1 320px', minWidth: 'min(260px, 100%)' }}>
            <input id="acc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or name" aria-label="Search accounts" />
          </label>
          <div className="el-chart-options">
            <label className="el-check"><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show switched-off accounts ({inactive})</label>
            <label className="el-check"><input type="checkbox" checked={showBalances} onChange={(e) => toggleBalances(e.target.checked)} /> Show balances</label>
          </div>
        </div>
        <div className="el-table-wrap">
          <table className="el-table el-report" aria-label="Accounts">
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={4}><div className="el-skeleton" style={{ height: 18 }} /></td></tr>}
              {CLASSES.map((c) => {
                const rows = shown.filter((a) => a.class === c.key);
                if (!rows.length) return null;
                const total = addMoney('0', ...rows.map((a) => naturalBalance(a)));
                return [
                  <tr key={c.key} className="el-report-head">
                    <td colSpan={2}>{c.label}</td>
                    <td className="el-num">{showBalances && <span aria-label={`${c.label} total`}><Money value={total} tone={compareMoney(total, '0') < 0 ? 'warn' : undefined} /></span>}</td>
                    <td />
                  </tr>,
                  ...rows.map((a) => (
                    <tr key={a.code} style={a.is_active ? undefined : { opacity: 0.6 }}>
                      <td><span className="el-report-code">{a.code}</span>{a.name}</td>
                      <td>
                        {CONTROL[a.control] && <Pill tone="info">{CONTROL[a.control]}</Pill>}
                        {!a.is_active && <Pill tone="draft">Switched off</Pill>}
                        {a.default_tax_code && <span className="el-hint" style={{ marginLeft: 8 }}>VAT {data.state === 'ready' ? data.data.codes.find((t) => t.code === a.default_tax_code)?.name ?? a.default_tax_code : ''}</span>}
                      </td>
                      <td className="el-num">{!showBalances || isZero(a.balance ?? '0') ? '' : <Link to={`/journals?account=${a.code}`}><Money value={naturalBalance(a)} tone={compareMoney(naturalBalance(a), '0') < 0 ? 'warn' : undefined} /></Link>}</td>
                      <td className="el-num">{mayEdit && <Button size="sm" variant="ghost" aria-label={`Edit ${a.code}`} onClick={() => setEditing(a)}>Edit</Button>}</td>
                    </tr>
                  )),
                ];
              })}
            </tbody>
          </table>
        </div>
      </Card>
      {editing && data.state === 'ready' && (
        <AccountForm account={editing === 'new' ? null : editing} codes={data.data.codes} onClose={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); toast(msg); setVersion((v) => v + 1); }} />
      )}
    </>
  );
}

function AccountForm({ account, codes, onClose, onSaved }: { account: Account | null; codes: TaxCode[]; onClose: () => void; onSaved: (msg: string) => void }) {
  const { source } = useSession();
  const [code, setCode] = useState('');
  const [cls, setCls] = useState<Account['class']>('expense');
  const [name, setName] = useState(account?.name ?? '');
  const [tax, setTax] = useState(account?.default_tax_code ?? '');
  const [active, setActive] = useState(account?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const control = account && account.control !== 'none';
  const nameLocked = !!account && account.control !== 'none' && account.control !== 'bank';
  const codeOk = /^\d{3,6}$/.test(code);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (account) {
        await source.updateAccount(account.code, {
          ...(name.trim() !== account.name ? { name: name.trim() } : {}),
          ...((tax || null) !== (account.default_tax_code ?? null) ? { default_tax_code: tax || null } : {}),
          ...(active !== account.is_active ? { is_active: active } : {}),
        });
        onSaved(active === account.is_active ? `${account.code} saved` : `${account.code} switched ${active ? 'on' : 'off'}`);
      } else {
        const a = await source.createAccount({ code, name: name.trim(), class: cls, default_tax_code: tax || undefined });
        onSaved(`${a.code} ${a.name} added`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Dialog title={account ? `Edit ${account.code}` : 'New account'} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !name.trim() || (!account && !codeOk)} onClick={() => void save()}>Save</Button></>}>
      {!account && (
        <div className="el-form-grid">
          <Field id="acc-code" label="Code" hint="3 to 6 digits; can’t change later" error={code && !codeOk ? '3 to 6 digits' : null}>
            <input id="acc-code" className="el-input" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value.trim())} />
          </Field>
          <Field id="acc-class" label="Type" hint="Can’t change once used">
            <select id="acc-class" className="el-input" value={cls} onChange={(e) => setCls(e.target.value as Account['class'])}>
              {CLASSES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
        </div>
      )}
      <Field id="acc-name" label="Name" hint={nameLocked ? 'Control accounts keep their names' : undefined}>
        <input id="acc-name" className="el-input" value={name} disabled={nameLocked} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field id="acc-vat" label="Default VAT" hint="Filled in when this account is chosen on an invoice or bill line">
        <select id="acc-vat" className="el-input" value={tax} onChange={(e) => setTax(e.target.value)}>
          <option value="">None</option>
          {codes.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
        </select>
      </Field>
      {account && !control && (
        <label className="el-check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> In use (untick to switch it off; past entries stay as they are)</label>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
